"use client";

// NOTE: This isn't the exact shadcn calendar: https://github.com/shadcn-ui/ui/issues/4361
import { c as _c } from "react/compiler-runtime";
import { differenceInCalendarDays } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { DayPicker, labelNext, labelPrevious, useDayPicker, type DayPickerProps } from "react-day-picker";
import { Button, buttonVariants } from "@/src/components/shadcn/ui/button";
import { cn } from "@/src/lib/shadcn-utils";
export type CalendarProps = DayPickerProps & {
  /**
   * In the year view, the number of years to display at once.
   * @default 12
   */
  yearRange?: number;

  /**
   * Whether to show the year switcher in the caption.
   * @default true
   */
  showYearSwitcher?: boolean;

  /**
   * Whether to highlight the current day.
   * @default true
   */
  highlightToday?: boolean;
  monthsClassName?: string;
  monthCaptionClassName?: string;
  weekdaysClassName?: string;
  weekdayClassName?: string;
  monthClassName?: string;
  captionClassName?: string;
  captionLabelClassName?: string;
  buttonNextClassName?: string;
  buttonPreviousClassName?: string;
  navClassName?: string;
  monthGridClassName?: string;
  weekClassName?: string;
  dayClassName?: string;
  dayButtonClassName?: string;
  rangeStartClassName?: string;
  rangeEndClassName?: string;
  selectedClassName?: string;
  todayClassName?: string;
  outsideClassName?: string;
  disabledClassName?: string;
  rangeMiddleClassName?: string;
  hiddenClassName?: string;
};
type NavView = "days" | "years";

/**
 * A custom calendar component built on top of react-day-picker.
 * @param props The props for the calendar.
 * @default yearRange 12
 * @returns
 */
function Calendar(t0) {
  const $ = _c(63);
  const {
    className,
    showOutsideDays: t1,
    showYearSwitcher: t2,
    yearRange: t3,
    highlightToday: t4,
    numberOfMonths,
    components,
    ...props
  } = t0;
  const showOutsideDays = t1 === undefined ? true : t1;
  const showYearSwitcher = t2 === undefined ? false : t2;
  const yearRange = t3 === undefined ? 12 : t3;
  const highlightToday = t4 === undefined ? false : t4;
  const [navView, setNavView] = useState("days");
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Date().getFullYear();
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const currentYear = t5;
  const t6 = currentYear - Math.floor(yearRange / 2 - 1);
  const t7 = currentYear + Math.ceil(yearRange / 2);
  let t8;
  if ($[1] !== t6 || $[2] !== t7) {
    t8 = {
      from: t6,
      to: t7
    };
    $[1] = t6;
    $[2] = t7;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const [displayYears, setDisplayYears] = useState(t8);
  const {
    onNextClick,
    onPrevClick,
    startMonth,
    endMonth
  } = props;
  const columnsDisplayed = navView === "years" ? 1 : numberOfMonths;
  const _monthsClassName = cn("relative flex", props.monthsClassName);
  const _monthCaptionClassName = cn("relative mx-10 flex h-7 items-center justify-center", props.monthCaptionClassName);
  const _weekdaysClassName = cn("flex flex-row", props.weekdaysClassName);
  const _weekdayClassName = cn("w-8 text-sm font-normal text-muted-foreground", props.weekdayClassName);
  const _monthClassName = cn("w-full", props.monthClassName);
  const _captionClassName = cn("relative flex items-center justify-center pt-1", props.captionClassName);
  const _captionLabelClassName = cn("truncate text-sm font-medium", props.captionLabelClassName);
  const buttonNavClassName = buttonVariants({
    variant: "outline",
    className: "absolute h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
  });
  const _buttonNextClassName = cn(buttonNavClassName, "right-0", props.buttonNextClassName);
  const _buttonPreviousClassName = cn(buttonNavClassName, "left-0", props.buttonPreviousClassName);
  const _navClassName = cn("flex items-start", props.navClassName);
  const _monthGridClassName = cn("mx-auto mt-4", props.monthGridClassName);
  const _weekClassName = cn("mt-2 flex w-max items-start", props.weekClassName);
  const _dayClassName = cn("flex size-8 flex-1 items-center justify-center p-0 text-sm", props.dayClassName);
  const _dayButtonClassName = cn(buttonVariants({
    variant: "ghost"
  }), "size-8 rounded-md p-0 font-normal transition-none aria-selected:opacity-100", props.dayButtonClassName);
  const _rangeStartClassName = cn("bg-accent [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground", "day-range-start rounded-s-md", props.rangeStartClassName);
  const _rangeEndClassName = cn("bg-accent [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground", "day-range-end rounded-e-md", props.rangeEndClassName);
  const _rangeMiddleClassName = cn("bg-accent !text-foreground [&>button]:bg-transparent [&>button]:!text-foreground [&>button]:hover:bg-transparent [&>button]:hover:!text-foreground", props.rangeMiddleClassName);
  const _selectedClassName = cn("[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground", props.selectedClassName);
  const _todayClassName = cn(highlightToday ? "[&>button]:bg-accent [&>button]:text-accent-foreground" : "", props.todayClassName);
  const _outsideClassName = cn("day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30", props.outsideClassName);
  const _disabledClassName = cn("text-muted-foreground opacity-50", props.disabledClassName);
  const _hiddenClassName = cn("invisible flex-1", props.hiddenClassName);
  let t9;
  if ($[4] !== className) {
    t9 = cn("p-3", className);
    $[4] = className;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const t10 = 248.8 * (columnsDisplayed ?? 1) + "px";
  let t11;
  if ($[6] !== t10) {
    t11 = {
      width: t10
    };
    $[6] = t10;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  let t12;
  if ($[8] !== _buttonNextClassName || $[9] !== _buttonPreviousClassName || $[10] !== _captionClassName || $[11] !== _captionLabelClassName || $[12] !== _dayButtonClassName || $[13] !== _dayClassName || $[14] !== _disabledClassName || $[15] !== _hiddenClassName || $[16] !== _monthCaptionClassName || $[17] !== _monthClassName || $[18] !== _monthGridClassName || $[19] !== _monthsClassName || $[20] !== _navClassName || $[21] !== _outsideClassName || $[22] !== _rangeEndClassName || $[23] !== _rangeMiddleClassName || $[24] !== _rangeStartClassName || $[25] !== _selectedClassName || $[26] !== _todayClassName || $[27] !== _weekClassName || $[28] !== _weekdayClassName || $[29] !== _weekdaysClassName) {
    t12 = {
      months: _monthsClassName,
      month_caption: _monthCaptionClassName,
      weekdays: _weekdaysClassName,
      weekday: _weekdayClassName,
      month: _monthClassName,
      caption: _captionClassName,
      caption_label: _captionLabelClassName,
      button_next: _buttonNextClassName,
      button_previous: _buttonPreviousClassName,
      nav: _navClassName,
      month_grid: _monthGridClassName,
      week: _weekClassName,
      day: _dayClassName,
      day_button: _dayButtonClassName,
      range_start: _rangeStartClassName,
      range_middle: _rangeMiddleClassName,
      range_end: _rangeEndClassName,
      selected: _selectedClassName,
      today: _todayClassName,
      outside: _outsideClassName,
      disabled: _disabledClassName,
      hidden: _hiddenClassName
    };
    $[8] = _buttonNextClassName;
    $[9] = _buttonPreviousClassName;
    $[10] = _captionClassName;
    $[11] = _captionLabelClassName;
    $[12] = _dayButtonClassName;
    $[13] = _dayClassName;
    $[14] = _disabledClassName;
    $[15] = _hiddenClassName;
    $[16] = _monthCaptionClassName;
    $[17] = _monthClassName;
    $[18] = _monthGridClassName;
    $[19] = _monthsClassName;
    $[20] = _navClassName;
    $[21] = _outsideClassName;
    $[22] = _rangeEndClassName;
    $[23] = _rangeMiddleClassName;
    $[24] = _rangeStartClassName;
    $[25] = _selectedClassName;
    $[26] = _todayClassName;
    $[27] = _weekClassName;
    $[28] = _weekdayClassName;
    $[29] = _weekdaysClassName;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== displayYears || $[32] !== endMonth || $[33] !== navView || $[34] !== onNextClick || $[35] !== onPrevClick || $[36] !== setDisplayYears || $[37] !== startMonth) {
    t13 = t14 => {
      const {
        className: className_0
      } = t14;
      return <Nav className={className_0} displayYears={displayYears} navView={navView} setDisplayYears={setDisplayYears} startMonth={startMonth} endMonth={endMonth} onPrevClick={onPrevClick} onNextClick={onNextClick} />;
    };
    $[31] = displayYears;
    $[32] = endMonth;
    $[33] = navView;
    $[34] = onNextClick;
    $[35] = onPrevClick;
    $[36] = setDisplayYears;
    $[37] = startMonth;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== displayYears || $[40] !== navView || $[41] !== setNavView || $[42] !== showYearSwitcher) {
    t14 = props_0 => <CaptionLabel showYearSwitcher={showYearSwitcher} navView={navView} setNavView={setNavView} displayYears={displayYears} {...props_0} />;
    $[39] = displayYears;
    $[40] = navView;
    $[41] = setNavView;
    $[42] = showYearSwitcher;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== displayYears || $[45] !== endMonth || $[46] !== navView || $[47] !== setNavView || $[48] !== startMonth) {
    t15 = t16 => {
      const {
        className: className_1,
        children,
        ...props_1
      } = t16;
      return <MonthGrid className={className_1} displayYears={displayYears} startMonth={startMonth} endMonth={endMonth} navView={navView} setNavView={setNavView} {...props_1}>{children}</MonthGrid>;
    };
    $[44] = displayYears;
    $[45] = endMonth;
    $[46] = navView;
    $[47] = setNavView;
    $[48] = startMonth;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== components || $[51] !== t13 || $[52] !== t14 || $[53] !== t15) {
    t16 = {
      Chevron: _temp,
      Nav: t13,
      CaptionLabel: t14,
      MonthGrid: t15,
      ...components
    };
    $[50] = components;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== columnsDisplayed || $[56] !== props || $[57] !== showOutsideDays || $[58] !== t11 || $[59] !== t12 || $[60] !== t16 || $[61] !== t9) {
    t17 = <DayPicker showOutsideDays={showOutsideDays} className={t9} style={t11} classNames={t12} components={t16} numberOfMonths={columnsDisplayed} {...props} />;
    $[55] = columnsDisplayed;
    $[56] = props;
    $[57] = showOutsideDays;
    $[58] = t11;
    $[59] = t12;
    $[60] = t16;
    $[61] = t9;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  return t17;
}
function _temp(t0) {
  const {
    orientation
  } = t0;
  const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
  return <Icon className="h-4 w-4" />;
}
Calendar.displayName = "Calendar";
function Nav(t0) {
  const $ = _c(48);
  const {
    className,
    navView,
    startMonth,
    endMonth,
    displayYears,
    setDisplayYears,
    onPrevClick,
    onNextClick
  } = t0;
  const {
    nextMonth,
    previousMonth,
    goToMonth
  } = useDayPicker();
  let t1;
  bb0: {
    if (navView === "years") {
      let t2;
      if ($[0] !== displayYears || $[1] !== endMonth || $[2] !== startMonth) {
        t2 = startMonth && differenceInCalendarDays(new Date(displayYears.from - 1, 0, 1), startMonth) < 0 || endMonth && differenceInCalendarDays(new Date(displayYears.from - 1, 0, 1), endMonth) > 0;
        $[0] = displayYears;
        $[1] = endMonth;
        $[2] = startMonth;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = t2;
      break bb0;
    }
    t1 = !previousMonth;
  }
  const isPreviousDisabled = t1;
  let t2;
  bb1: {
    if (navView === "years") {
      let t3;
      if ($[4] !== displayYears || $[5] !== endMonth || $[6] !== startMonth) {
        t3 = startMonth && differenceInCalendarDays(new Date(displayYears.to + 1, 0, 1), startMonth) < 0 || endMonth && differenceInCalendarDays(new Date(displayYears.to + 1, 0, 1), endMonth) > 0;
        $[4] = displayYears;
        $[5] = endMonth;
        $[6] = startMonth;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      t2 = t3;
      break bb1;
    }
    t2 = !nextMonth;
  }
  const isNextDisabled = t2;
  let t3;
  if ($[8] !== displayYears || $[9] !== goToMonth || $[10] !== navView || $[11] !== onPrevClick || $[12] !== previousMonth || $[13] !== setDisplayYears) {
    t3 = () => {
      if (!previousMonth) {
        return;
      }
      if (navView === "years") {
        setDisplayYears(_temp2);
        onPrevClick?.(new Date(displayYears.from - (displayYears.to - displayYears.from), 0, 1));
        return;
      }
      goToMonth(previousMonth);
      onPrevClick?.(previousMonth);
    };
    $[8] = displayYears;
    $[9] = goToMonth;
    $[10] = navView;
    $[11] = onPrevClick;
    $[12] = previousMonth;
    $[13] = setDisplayYears;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const handlePreviousClick = t3;
  let t4;
  if ($[15] !== displayYears || $[16] !== goToMonth || $[17] !== navView || $[18] !== nextMonth || $[19] !== onNextClick || $[20] !== setDisplayYears) {
    t4 = () => {
      if (!nextMonth) {
        return;
      }
      if (navView === "years") {
        setDisplayYears(_temp3);
        onNextClick?.(new Date(displayYears.from + (displayYears.to - displayYears.from), 0, 1));
        return;
      }
      goToMonth(nextMonth);
      onNextClick?.(nextMonth);
    };
    $[15] = displayYears;
    $[16] = goToMonth;
    $[17] = navView;
    $[18] = nextMonth;
    $[19] = onNextClick;
    $[20] = setDisplayYears;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  const handleNextClick = t4;
  let t5;
  if ($[22] !== className) {
    t5 = cn("flex items-center", className);
    $[22] = className;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const t6 = isPreviousDisabled ? undefined : -1;
  let t7;
  if ($[24] !== displayYears || $[25] !== navView || $[26] !== previousMonth) {
    t7 = navView === "years" ? `Go to the previous ${displayYears.to - displayYears.from + 1} years` : labelPrevious(previousMonth);
    $[24] = displayYears;
    $[25] = navView;
    $[26] = previousMonth;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChevronLeft className="h-4 w-4" />;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== handlePreviousClick || $[30] !== isPreviousDisabled || $[31] !== t6 || $[32] !== t7) {
    t9 = <Button variant="outline" className="absolute left-0 h-7 w-7 bg-transparent p-0 opacity-80 hover:opacity-100" type="button" tabIndex={t6} disabled={isPreviousDisabled} aria-label={t7} onClick={handlePreviousClick}>{t8}</Button>;
    $[29] = handlePreviousClick;
    $[30] = isPreviousDisabled;
    $[31] = t6;
    $[32] = t7;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const t10 = isNextDisabled ? undefined : -1;
  let t11;
  if ($[34] !== displayYears || $[35] !== navView || $[36] !== nextMonth) {
    t11 = navView === "years" ? `Go to the next ${displayYears.to - displayYears.from + 1} years` : labelNext(nextMonth);
    $[34] = displayYears;
    $[35] = navView;
    $[36] = nextMonth;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronRight className="h-4 w-4" />;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== handleNextClick || $[40] !== isNextDisabled || $[41] !== t10 || $[42] !== t11) {
    t13 = <Button variant="outline" className="absolute right-0 h-7 w-7 bg-transparent p-0 opacity-80 hover:opacity-100" type="button" tabIndex={t10} disabled={isNextDisabled} aria-label={t11} onClick={handleNextClick}>{t12}</Button>;
    $[39] = handleNextClick;
    $[40] = isNextDisabled;
    $[41] = t10;
    $[42] = t11;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t13 || $[45] !== t5 || $[46] !== t9) {
    t14 = <nav className={t5}>{t9}{t13}</nav>;
    $[44] = t13;
    $[45] = t5;
    $[46] = t9;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
}
function _temp3(prev_0) {
  return {
    from: prev_0.from + (prev_0.to - prev_0.from + 1),
    to: prev_0.to + (prev_0.to - prev_0.from + 1)
  };
}
function _temp2(prev) {
  return {
    from: prev.from - (prev.to - prev.from + 1),
    to: prev.to - (prev.to - prev.from + 1)
  };
}
function CaptionLabel(t0) {
  const $ = _c(15);
  let children;
  let displayYears;
  let navView;
  let props;
  let setNavView;
  let showYearSwitcher;
  if ($[0] !== t0) {
    ({
      children,
      showYearSwitcher,
      navView,
      setNavView,
      displayYears,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = displayYears;
    $[3] = navView;
    $[4] = props;
    $[5] = setNavView;
    $[6] = showYearSwitcher;
  } else {
    children = $[1];
    displayYears = $[2];
    navView = $[3];
    props = $[4];
    setNavView = $[5];
    showYearSwitcher = $[6];
  }
  if (!showYearSwitcher) {
    let t1;
    if ($[7] !== children || $[8] !== props) {
      t1 = <span {...props}>{children}</span>;
      $[7] = children;
      $[8] = props;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    return t1;
  }
  let t1;
  if ($[10] !== setNavView) {
    t1 = () => setNavView(_temp4);
    $[10] = setNavView;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const t2 = navView === "days" ? children : displayYears.from + " - " + displayYears.to;
  let t3;
  if ($[12] !== t1 || $[13] !== t2) {
    t3 = <Button className="h-7 w-full truncate text-sm font-medium" variant="ghost" size="sm" onClick={t1}>{t2}</Button>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
function _temp4(prev) {
  return prev === "days" ? "years" : "days";
}
function MonthGrid(t0) {
  const $ = _c(21);
  let children;
  let className;
  let displayYears;
  let endMonth;
  let navView;
  let props;
  let setNavView;
  let startMonth;
  if ($[0] !== t0) {
    ({
      className,
      children,
      displayYears,
      startMonth,
      endMonth,
      navView,
      setNavView,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = displayYears;
    $[4] = endMonth;
    $[5] = navView;
    $[6] = props;
    $[7] = setNavView;
    $[8] = startMonth;
  } else {
    children = $[1];
    className = $[2];
    displayYears = $[3];
    endMonth = $[4];
    navView = $[5];
    props = $[6];
    setNavView = $[7];
    startMonth = $[8];
  }
  if (navView === "years") {
    let t1;
    if ($[9] !== className || $[10] !== displayYears || $[11] !== endMonth || $[12] !== navView || $[13] !== props || $[14] !== setNavView || $[15] !== startMonth) {
      t1 = <YearGrid displayYears={displayYears} startMonth={startMonth} endMonth={endMonth} setNavView={setNavView} navView={navView} className={className} {...props} />;
      $[9] = className;
      $[10] = displayYears;
      $[11] = endMonth;
      $[12] = navView;
      $[13] = props;
      $[14] = setNavView;
      $[15] = startMonth;
      $[16] = t1;
    } else {
      t1 = $[16];
    }
    return t1;
  }
  let t1;
  if ($[17] !== children || $[18] !== className || $[19] !== props) {
    t1 = <table className={className} {...props}>{children}</table>;
    $[17] = children;
    $[18] = className;
    $[19] = props;
    $[20] = t1;
  } else {
    t1 = $[20];
  }
  return t1;
}
function YearGrid(t0) {
  const $ = _c(31);
  let className;
  let displayYears;
  let endMonth;
  let navView;
  let props;
  let setNavView;
  let startMonth;
  if ($[0] !== t0) {
    ({
      className,
      displayYears,
      startMonth,
      endMonth,
      setNavView,
      navView,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = displayYears;
    $[3] = endMonth;
    $[4] = navView;
    $[5] = props;
    $[6] = setNavView;
    $[7] = startMonth;
  } else {
    className = $[1];
    displayYears = $[2];
    endMonth = $[3];
    navView = $[4];
    props = $[5];
    setNavView = $[6];
    startMonth = $[7];
  }
  const {
    goToMonth,
    selected
  } = useDayPicker();
  let t1;
  if ($[8] !== className) {
    t1 = cn("grid grid-cols-4 gap-y-2", className);
    $[8] = className;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== displayYears.from || $[11] !== displayYears.to || $[12] !== endMonth || $[13] !== goToMonth || $[14] !== navView || $[15] !== selected || $[16] !== setNavView || $[17] !== startMonth) {
    let t3;
    if ($[19] !== displayYears.from || $[20] !== endMonth || $[21] !== goToMonth || $[22] !== navView || $[23] !== selected || $[24] !== setNavView || $[25] !== startMonth) {
      t3 = (_, i) => {
        const isBefore = differenceInCalendarDays(new Date(displayYears.from + i, 11, 31), startMonth) < 0;
        const isAfter = differenceInCalendarDays(new Date(displayYears.from + i, 0, 0), endMonth) > 0;
        const isDisabled = isBefore || isAfter;
        return <Button key={i} className={cn("h-7 w-full text-sm font-normal text-foreground", displayYears.from + i === new Date().getFullYear() && "bg-accent font-medium text-accent-foreground")} variant="ghost" onClick={() => {
          setNavView("days");
          goToMonth(new Date(displayYears.from + i, (selected as Date | undefined)?.getMonth() ?? 0));
        }} disabled={navView === "years" ? isDisabled : undefined}>{displayYears.from + i}</Button>;
      };
      $[19] = displayYears.from;
      $[20] = endMonth;
      $[21] = goToMonth;
      $[22] = navView;
      $[23] = selected;
      $[24] = setNavView;
      $[25] = startMonth;
      $[26] = t3;
    } else {
      t3 = $[26];
    }
    t2 = Array.from({
      length: displayYears.to - displayYears.from + 1
    }, t3);
    $[10] = displayYears.from;
    $[11] = displayYears.to;
    $[12] = endMonth;
    $[13] = goToMonth;
    $[14] = navView;
    $[15] = selected;
    $[16] = setNavView;
    $[17] = startMonth;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[27] !== props || $[28] !== t1 || $[29] !== t2) {
    t3 = <div className={t1} {...props}>{t2}</div>;
    $[27] = props;
    $[28] = t1;
    $[29] = t2;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  return t3;
}
export { Calendar };